import React, { useState, useEffect } from 'react';
import { useAuth } from '../../../../context/AuthContext';
import { Clock, Briefcase, Building2, Coffee } from 'lucide-react';
import { useSocket } from '../../../../hooks/useSocket';
import { useTimezone } from '../../../../hooks/useTimezone';
import { formatInTimezone, formatTimeOnly } from '../../../../utils/timezoneUtils';
import TeamManagerDashboard from '../../../../components/TeamManagerDashboard';

const ClockIn = () => {
  const { currentUser } = useAuth();
  const userTimezone = useTimezone();
  const [currentTime, setCurrentTime] = useState(new Date());
  const [clockedIn, setClockedIn] = useState(false);
  const [onBreak, setOnBreak] = useState(false);
  const [onLunch, setOnLunch] = useState(false);
  const [clockInTime, setClockInTime] = useState(null);
  const [clockOutTime, setClockOutTime] = useState(null);
  const [breakStartTime, setBreakStartTime] = useState(null);
  const [lunchStartTime, setLunchStartTime] = useState(null);
  const [selectedActivity, setSelectedActivity] = useState('');
  const [selectedBusinessUnit, setSelectedBusinessUnit] = useState('');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [success, setSuccess] = useState(null);
  const [userTeam, setUserTeam] = useState(null);
  const [userShift, setUserShift] = useState(null);
  const [activities, setActivities] = useState([]);
  const [businessUnits, setBusinessUnits] = useState([]);
  const [currentSession, setCurrentSession] = useState(null);
  
  
  // Team Manager States
  const [isTeamManager, setIsTeamManager] = useState(false);
  const [managedTeams, setManagedTeams] = useState([]);
  const [selectedTeam, setSelectedTeam] = useState(null);
  const [teamMembers, setTeamMembers] = useState([]);
  const [teamMemberSessions, setTeamMemberSessions] = useState({});
  const [loadingTeamData, setLoadingTeamData] = useState(false);
  

  // Socket connection
  const { socket, isConnected } = useSocket(currentUser?.id);

  // Get API URL from environment or default
  const API_URL = process.env.REACT_APP_API_URL || 'https://ksp3plhq.com/api';

  // Update current time every second
  useEffect(() => {
    const timer = setInterval(() => {
      setCurrentTime(new Date());
    }, 1000);

    return () => {
      clearInterval(timer);
    };
  }, []);

  // Listen for WebSocket updates
  useEffect(() => {
    if (!socket || !currentUser) return;
    
    const handleClockStatusUpdate = (data) => {
      console.log('Received clock status update:', data);
      
      if (data.userId === currentUser.id) {
        setClockedIn(data.isClocked);
        
        if (data.entry) {
          setCurrentSession(data.entry);
          
          if (data.isClocked) {
            setClockInTime(new Date(data.entry.clock_in_time));
            
            // Set activity and business unit if not IDLE
            if (data.entry.activity && data.entry.activity !== 'IDLE') {
              setSelectedActivity(data.entry.activity);
            }
            
            if (data.entry.business_unit && data.entry.business_unit !== 'IDLE') {
              setSelectedBusinessUnit(data.entry.business_unit);
            }
            
            // Check if on break or lunch
            setOnBreak(data.entry.activity === 'BREAK' || data.entry.activity === 'Break');
            setOnLunch(data.entry.activity === 'LUNCH' || data.entry.activity === 'Lunch');
          }
        }
        
      }
    };
    
    // Handle activity updates
    const handleActivityUpdate = (data) => {
      console.log('Received activity update:', data);
      if (data.activity) {
        setActivities(prevActivities => {
          const updatedActivities = prevActivities.map(activity =>
            activity.id === data.activity.id ? data.activity : activity
          );
          return updatedActivities;
        });
      }
    };

    // Register event listeners
    socket.on('update-clock-status', handleClockStatusUpdate);
    socket.on('activity-update', handleActivityUpdate);
    
    return () => {
      socket.off('update-clock-status', handleClockStatusUpdate);
      socket.off('activity-update', handleActivityUpdate);
    };
  }, [socket, currentUser]);

  useEffect(() => {
    const fetchInitialData = async () => {
      setLoading(true);
      try {
        // Fetch activities
        const activitiesResponse = await fetch(`${API_URL}/activities`, {
          headers: {
            'Authorization': `Bearer ${localStorage.getItem('token')}`
          }
        });
        
        if (!activitiesResponse.ok) {
          throw new Error('Failed to fetch activities');
        }
        
        const activitiesData = await activitiesResponse.json();
        setActivities(activitiesData);
        
        // Fetch business units
        const businessUnitsResponse = await fetch(`${API_URL}/business-units`, {
          headers: {
            'Authorization': `Bearer ${localStorage.getItem('token')}`
          }
        });
        
        if (!businessUnitsResponse.ok) {
          throw new Error('Failed to fetch business units');
        }
        
        const businessUnitsData = await businessUnitsResponse.json();
        setBusinessUnits(businessUnitsData);
        
        // Check if user has an active session
        await checkCurrentSession();
        
        
        // Add this line to fetch user assignments
        await fetchUserAssignments();
        
      } catch (err) {
        console.error('Error fetching initial data:', err);
        setError('Failed to load initial data. Please refresh and try again.');
      } finally {
        setLoading(false);
      }
    };
    
    if (currentUser) {
      fetchInitialData();
    }
  }, [API_URL, currentUser]);

  // Check for current active session
  const checkCurrentSession = async () => {
    if (!currentUser) return;
    
    try {
      const response = await fetch(`${API_URL}/labor/current/${currentUser.id}`, {
        headers: {
          'Authorization': `Bearer ${localStorage.getItem('token')}`
        }
      });
      
      if (!response.ok) {
        throw new Error('Failed to fetch current session');
      }
      
      const data = await response.json();
      
      if (data.active && data.entry) {
        setCurrentSession(data.entry);
        setClockedIn(true);
        setClockInTime(new Date(data.entry.clock_in_time));
        
        // Set activity and business unit if not IDLE
        if (data.entry.activity && data.entry.activity !== 'IDLE') {
          setSelectedActivity(data.entry.activity);
        }
        
        if (data.entry.business_unit && data.entry.business_unit !== 'IDLE') {
          setSelectedBusinessUnit(data.entry.business_unit);
        }
        
        // Check if on break or lunch
        setOnBreak(data.entry.activity === 'BREAK');
        setOnLunch(data.entry.activity === 'LUNCH');
      } else {
        setCurrentSession(null);
        setClockedIn(false);
      }
    } catch (err) {
      console.error('Error checking current session:', err);
    }
  };


  const handleClockIn = async () => {
    setLoading(true);
    setError(null);
    setSuccess(null);
    
    try {
      if (!clockedIn) {
        // Clock in - create new labor entry with IDLE activity/business unit
        const response = await fetch(`${API_URL}/labor`, {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            'Authorization': `Bearer ${localStorage.getItem('token')}`
          },
          body: JSON.stringify({
            employee_id: currentUser.id,
            full_name: currentUser.fullName || currentUser.username,
            activity: 'IDLE',
            business_unit: 'IDLE',
            team: userTeam || null, 
            shift: userShift || null 
          })
        });
        
        if (!response.ok) {
          throw new Error('Failed to clock in');
        }
        
        const data = await response.json();
        setCurrentSession(data.entry);
        setClockedIn(true);
        setClockInTime(new Date(data.entry.clock_in_time));
        setSuccess('Clocked in successfully');
        
        // Note: We don't need to update state here as it will be updated via WebSocket
      } else {
        // Clock out - update current session
        const response = await fetch(`${API_URL}/labor/${currentSession.id}/clockout`, {
          method: 'PUT',
          headers: {
            'Authorization': `Bearer ${localStorage.getItem('token')}`
          }
        });
        
        if (!response.ok) {
          throw new Error('Failed to clock out');
        }
        
        setClockOutTime(new Date());
        setSuccess('Clocked out successfully');
        
        // Note: We don't need to update state here as it will be updated via WebSocket
      }
      
    } catch (err) {
      console.error('Error with clock in/out:', err);
      setError(err.message || 'An error occurred. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  const handleBreak = async () => {
    if (!clockedIn) return;
    
    setLoading(true);
    setError(null);
    setSuccess(null);
    
    try {
      if (!onBreak) {
        // Start break - keep current business unit
        await createNewSession('Break', currentSession.business_unit);
        setOnBreak(true);
        setBreakStartTime(new Date());
        setSuccess('Break started');
      } else {
        // End break - restore previous work activity
        try {
          const response = await fetch(`${API_URL}/labor/previous-activity/${currentUser.id}`, {
            headers: {
              'Authorization': `Bearer ${localStorage.getItem('token')}`
            }
          });
          
          if (response.ok) {
            const { activity, business_unit, project } = await response.json();
            await createNewSession(activity, business_unit, project);
            setSelectedActivity(activity);
            setSelectedBusinessUnit(business_unit);
            setOnBreak(false);
            setSuccess(activity === 'IDLE' ? 
              'Break ended - No previous activity found, set to IDLE' : 
              `Break ended - Returned to ${activity} / ${business_unit}`);
          } else {
            // Fallback to IDLE if API call fails
            await createNewSession('IDLE', 'IDLE');
            setSelectedActivity('');
            setSelectedBusinessUnit('');
            setOnBreak(false);
            setSuccess('Break ended - Set to IDLE');
          }
        } catch (err) {
          // Fallback to IDLE if there's an error
          console.error('Error getting previous activity:', err);
          await createNewSession('IDLE', 'IDLE');
          setSelectedActivity('');
          setSelectedBusinessUnit('');
          setOnBreak(false);
          setSuccess('Break ended - Set to IDLE');
        }
      }
      
      // Note: Additional state will be updated via WebSocket
      
    } catch (err) {
      console.error('Error with break:', err);
      setError(err.message || 'An error occurred. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  // New handler function for lunch breaks
  const handleLunch = async () => {
    if (!clockedIn) return;
    
    setLoading(true);
    setError(null);
    setSuccess(null);
    
    try {
      if (!onLunch) {
        // Start lunch - keep current business unit
        await createNewSession('Lunch', currentSession.business_unit);
        setOnLunch(true);
        setLunchStartTime(new Date());
        setSuccess('Lunch started');
      } else {
        // End lunch - restore previous work activity
        try {
          const response = await fetch(`${API_URL}/labor/previous-activity/${currentUser.id}`, {
            headers: {
              'Authorization': `Bearer ${localStorage.getItem('token')}`
            }
          });
          
          if (response.ok) {
            const { activity, business_unit, project } = await response.json();
            await createNewSession(activity, business_unit, project);
            setSelectedActivity(activity);
            setSelectedBusinessUnit(business_unit);
            setOnLunch(false);
            setSuccess(activity === 'IDLE' ? 
              'Lunch ended - No previous activity found, set to IDLE' : 
              `Lunch ended - Returned to ${activity} / ${business_unit}`);
          } else {
            // Fallback to IDLE if API call fails
            await createNewSession('IDLE', 'IDLE');
            setSelectedActivity('');
            setSelectedBusinessUnit('');
            setOnLunch(false);
            setSuccess('Lunch ended - Set to IDLE');
          }
        } catch (err) {
          // Fallback to IDLE if there's an error
          console.error('Error getting previous activity:', err);
          await createNewSession('IDLE', 'IDLE');
          setSelectedActivity('');
          setSelectedBusinessUnit('');
          setOnLunch(false);
          setSuccess('Lunch ended - Set to IDLE');
        }
      }
      
      // Note: Additional state will be updated via WebSocket
      
    } catch (err) {
      console.error('Error with lunch:', err);
      setError(err.message || 'An error occurred. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  // Handle activity change
  const handleActivityChange = async (e) => {
    if (!clockedIn) return;
    
    const newActivity = e.target.value;
    setLoading(true);
    setError(null);
    setSuccess(null);
    
    try {
      // Create new session with updated activity
      await createNewSession(newActivity, selectedBusinessUnit || 'IDLE');
      setSelectedActivity(newActivity);
      setSuccess(`Activity updated to ${newActivity}`);
      
      // Note: Additional state will be updated via WebSocket
      
    } catch (err) {
      console.error('Error updating activity:', err);
      setError(err.message || 'Failed to update activity. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  // Handle business unit change
  const handleBusinessUnitChange = async (e) => {
    if (!clockedIn) return;
    
    const newBusinessUnit = e.target.value;
    setLoading(true);
    setError(null);
    setSuccess(null);
    
    try {
      // Create new session with updated business unit
      await createNewSession(selectedActivity || 'IDLE', newBusinessUnit);
      setSelectedBusinessUnit(newBusinessUnit);
      setSuccess(`Business unit updated to ${newBusinessUnit}`);
      
      // Note: Additional state will be updated via WebSocket
      
    } catch (err) {
      console.error('Error updating business unit:', err);
      setError(err.message || 'Failed to update business unit. Please try again.');
    } finally {
      setLoading(false);
    }
  };

// Modified function in ClockIn.js
const createNewSession = async (activity, businessUnit, project = null) => {
  // First, close the current session
  if (currentSession) {
    const closeResponse = await fetch(`${API_URL}/labor/${currentSession.id}/clockout`, {
      method: 'PUT',
      headers: {
        'Authorization': `Bearer ${localStorage.getItem('token')}`
      }
    });
    
    if (!closeResponse.ok) {
      throw new Error('Failed to close current session');
    }
  }
  
  // Then create a new session
  const createResponse = await fetch(`${API_URL}/labor`, {
    method: 'POST',
    headers: {
      'Content-Type': 'application/json',
      'Authorization': `Bearer ${localStorage.getItem('token')}`
    },
    body: JSON.stringify({
      employee_id: currentUser.id,
      full_name: currentUser.fullName || currentUser.username,
      activity,
      business_unit: businessUnit,
      project: project || currentSession?.project, // Use provided project or retain current
      group: currentSession?.group, // Include current group if available
      team: userTeam || null,
      shift: userShift || null
    })
  });
  
  if (!createResponse.ok) {
    throw new Error('Failed to create new session');
  }
  
  const data = await createResponse.json();
  setCurrentSession(data.entry);
  setClockInTime(new Date(data.entry.clock_in_time));
  
  return data.entry;
};


  const formatTime = (date) => {
    if (!date) return '';
    return formatTimeOnly(date, userTimezone).replace(' CST', '').replace(' CDT', '');
  };

  const formatDate = (date) => {
    if (!date) return '';
    return formatInTimezone(date, userTimezone, { 
      weekday: 'long', 
      month: 'long', 
      day: 'numeric', 
      year: 'numeric',
      hour: undefined,
      minute: undefined,
      timeZoneName: undefined
    });
  };

  // Helper function to convert local timezone date to UTC for server storage
  const convertToUTC = (date) => {
    if (!date) return null;
    return new Date(date).toISOString();
  };


  // Fetch user's team and shift assignments
  const fetchUserAssignments = async () => {
    if (!currentUser) return;
    
    try {
      // Fetch team assignment
      const teamResponse = await fetch(`${API_URL}/users/${currentUser.id}/team`, {
        headers: {
          'Authorization': `Bearer ${localStorage.getItem('token')}`
        }
      });
      
      if (teamResponse.ok) {
        const teamData = await teamResponse.json();
        setUserTeam(teamData.team);
      }
      
      // Fetch shift assignment
      const shiftResponse = await fetch(`${API_URL}/users/${currentUser.id}/shift`, {
        headers: {
          'Authorization': `Bearer ${localStorage.getItem('token')}`
        }
      });
      
      if (shiftResponse.ok) {
        const shiftData = await shiftResponse.json();
        setUserShift(shiftData.shift);
      }
      
      // Check if user is a team manager
      await checkIfTeamManager();
      
    } catch (err) {
      console.error('Error fetching user assignments:', err);
    }
  };
  
  // Check if current user is a team manager
  const checkIfTeamManager = async () => {
    if (!currentUser) return;
    
    try {
      setLoadingTeamData(true);
      const response = await fetch(`${API_URL}/teams/managed-by/${currentUser.id}`, {
        headers: {
          'Authorization': `Bearer ${localStorage.getItem('token')}`
        }
      });
      
      if (response.ok) {
        const teams = await response.json();
        if (teams.length > 0) {
          setIsTeamManager(true);
          setManagedTeams(teams);
          setSelectedTeam(teams[0]); // Select first team by default
          await fetchTeamMemberSessions(teams[0].id);
        }
      }
    } catch (err) {
      console.error('Error checking team manager status:', err);
    } finally {
      setLoadingTeamData(false);
    }
  };
  
  // Fetch active sessions for team members
  const fetchTeamMemberSessions = async (teamId) => {
    if (!teamId) return;
    
    try {
      setLoadingTeamData(true);
      const response = await fetch(`${API_URL}/teams/${teamId}/active-members`, {
        headers: {
          'Authorization': `Bearer ${localStorage.getItem('token')}`
        }
      });
      
      if (response.ok) {
        const data = await response.json();
        setTeamMembers(data.members || []);
        
        // Create a map of employee_id to session for easy lookup
        const sessionsMap = {};
        data.members.forEach(member => {
          sessionsMap[member.employee_id] = member;
        });
        setTeamMemberSessions(sessionsMap);
      }
    } catch (err) {
      console.error('Error fetching team member sessions:', err);
    } finally {
      setLoadingTeamData(false);
    }
  };
  
  // Handle team member break toggle
  const handleTeamMemberBreak = async (memberId) => {
    if (!selectedTeam) return;
    
    try {
      setLoading(true);
      
      // If no specific member ID provided, handle all active team members
      const membersToUpdate = memberId ? [memberId] : teamMembers.filter(member => {
        const session = teamMemberSessions[member.employee_id];
        return session?.clock_in_time && !session?.clock_out_time;
      }).map(member => member.employee_id);
      
      const token = localStorage.getItem('token');
      
      // Process each member
      for (const empId of membersToUpdate) {
        const session = teamMemberSessions[empId];
        if (!session) continue;
        
        const isOnBreak = session.activity === 'BREAK' || session.activity === 'Break';
        
        if (isOnBreak) {
          // End break - clock out current session
          await fetch(`${API_URL}/labor/${session.id}/clockout`, {
            method: 'PUT',
            headers: {
              'Authorization': `Bearer ${token}`
            }
          });
          
          // Get previous activity for this employee
          const prevActivityResponse = await fetch(`${API_URL}/labor/previous-activity/${empId}`, {
            headers: {
              'Authorization': `Bearer ${token}`
            }
          });
          
          let activity = 'IDLE';
          let business_unit = 'IDLE';
          
          if (prevActivityResponse.ok) {
            const prevActivity = await prevActivityResponse.json();
            activity = prevActivity.activity;
            business_unit = prevActivity.business_unit;
          }
          
          // Clock in with restored or IDLE activity
          await fetch(`${API_URL}/labor`, {
            method: 'POST',
            headers: {
              'Content-Type': 'application/json',
              'Authorization': `Bearer ${token}`
            },
            body: JSON.stringify({
              employee_id: empId,
              full_name: teamMembers.find(m => m.employee_id === empId)?.full_name || '',
              activity: activity,
              business_unit: business_unit,
              group: null,
              shift: null,
              team: selectedTeam.name
            })
          });
        } else {
          // Start break - clock out current session first
          await fetch(`${API_URL}/labor/${session.id}/clockout`, {
            method: 'PUT',
            headers: {
              'Authorization': `Bearer ${token}`
            }
          });
          
          // Clock in with BREAK, keeping current business unit
          await fetch(`${API_URL}/labor`, {
            method: 'POST',
            headers: {
              'Content-Type': 'application/json',
              'Authorization': `Bearer ${token}`
            },
            body: JSON.stringify({
              employee_id: empId,
              full_name: teamMembers.find(m => m.employee_id === empId)?.full_name || '',
              activity: 'BREAK',
              business_unit: session.business_unit,
              group: session.group,
              project: session.project,
              shift: session.shift,
              team: session.team
            })
          });
        }
      }
      
      setSuccess('Team break status updated');
      // Refresh team member sessions
      await fetchTeamMemberSessions(selectedTeam.id);
    } catch (err) {
      console.error('Error toggling team break:', err);
      setError(err.message || 'Failed to update team break status');
    } finally {
      setLoading(false);
    }
  };
  
  // Handle team member lunch toggle
  const handleTeamMemberLunch = async (memberId) => {
    if (!selectedTeam) return;
    
    try {
      setLoading(true);
      
      // If no specific member ID provided, handle all active team members
      const membersToUpdate = memberId ? [memberId] : teamMembers.filter(member => {
        const session = teamMemberSessions[member.employee_id];
        return session?.clock_in_time && !session?.clock_out_time;
      }).map(member => member.employee_id);
      
      const token = localStorage.getItem('token');
      
      // Process each member
      for (const empId of membersToUpdate) {
        const session = teamMemberSessions[empId];
        if (!session) continue;
        
        const isOnLunch = session.activity === 'LUNCH' || session.activity === 'Lunch';
        
        if (isOnLunch) {
          // End lunch - clock out current session
          await fetch(`${API_URL}/labor/${session.id}/clockout`, {
            method: 'PUT',
            headers: {
              'Authorization': `Bearer ${token}`
            }
          });
          
          // Get previous activity for this employee
          const prevActivityResponse = await fetch(`${API_URL}/labor/previous-activity/${empId}`, {
            headers: {
              'Authorization': `Bearer ${token}`
            }
          });
          
          let activity = 'IDLE';
          let business_unit = 'IDLE';
          
          if (prevActivityResponse.ok) {
            const prevActivity = await prevActivityResponse.json();
            activity = prevActivity.activity;
            business_unit = prevActivity.business_unit;
          }
          
          // Clock in with restored or IDLE activity
          await fetch(`${API_URL}/labor`, {
            method: 'POST',
            headers: {
              'Content-Type': 'application/json',
              'Authorization': `Bearer ${token}`
            },
            body: JSON.stringify({
              employee_id: empId,
              full_name: teamMembers.find(m => m.employee_id === empId)?.full_name || '',
              activity: activity,
              business_unit: business_unit,
              group: null,
              shift: null,
              team: selectedTeam.name
            })
          });
        } else {
          // Start lunch - clock out current session first
          await fetch(`${API_URL}/labor/${session.id}/clockout`, {
            method: 'PUT',
            headers: {
              'Authorization': `Bearer ${token}`
            }
          });
          
          // Clock in with LUNCH, keeping current business unit
          await fetch(`${API_URL}/labor`, {
            method: 'POST',
            headers: {
              'Content-Type': 'application/json',
              'Authorization': `Bearer ${token}`
            },
            body: JSON.stringify({
              employee_id: empId,
              full_name: teamMembers.find(m => m.employee_id === empId)?.full_name || '',
              activity: 'LUNCH',
              business_unit: session.business_unit,
              group: session.group,
              project: session.project,
              shift: session.shift,
              team: session.team
            })
          });
        }
      }
      
      setSuccess('Team lunch status updated');
      // Refresh team member sessions
      await fetchTeamMemberSessions(selectedTeam.id);
    } catch (err) {
      console.error('Error toggling team lunch:', err);
      setError(err.message || 'Failed to update team lunch status');
    } finally {
      setLoading(false);
    }
  };
  
  // Handle team member activity update
  const handleTeamMemberActivityChange = async (memberId, sessionId, activity) => {
    try {
      setLoading(true);
      const response = await fetch(`${API_URL}/labor/${sessionId}`, {
        method: 'PUT',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${localStorage.getItem('token')}`
        },
        body: JSON.stringify({
          activity,
          notes: `Activity changed by manager ${currentUser.username}`
        })
      });
      
      if (!response.ok) {
        throw new Error('Failed to update activity');
      }
      
      setSuccess('Activity updated successfully');
      // Refresh team member sessions
      await fetchTeamMemberSessions(selectedTeam.id);
    } catch (err) {
      console.error('Error updating activity:', err);
      setError(err.message || 'Failed to update activity');
    } finally {
      setLoading(false);
    }
  };
  
  // Handle team member business unit update
  const handleTeamMemberBusinessUnitChange = async (memberId, sessionId, businessUnit) => {
    try {
      setLoading(true);
      const response = await fetch(`${API_URL}/labor/${sessionId}`, {
        method: 'PUT',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${localStorage.getItem('token')}`
        },
        body: JSON.stringify({
          business_unit: businessUnit,
          notes: `Business unit changed by manager ${currentUser.username}`
        })
      });
      
      if (!response.ok) {
        throw new Error('Failed to update business unit');
      }
      
      setSuccess('Business unit updated successfully');
      // Refresh team member sessions
      await fetchTeamMemberSessions(selectedTeam.id);
    } catch (err) {
      console.error('Error updating business unit:', err);
      setError(err.message || 'Failed to update business unit');
    } finally {
      setLoading(false);
    }
  };
  
  // Handle team-wide activity change
  const handleTeamActivity = async (activity) => {
    if (!selectedTeam) return;
    
    try {
      setLoading(true);
      const response = await fetch(`${API_URL}/labor/teams/${selectedTeam.id}/activity`, {
        method: 'PUT',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${localStorage.getItem('token')}`
        },
        body: JSON.stringify({
          activity,
          notes: `Team activity changed by manager ${currentUser.username}`
        })
      });
      
      if (!response.ok) {
        throw new Error('Failed to update team activity');
      }
      
      setSuccess(`Team activity changed to ${activity}`);
      // Refresh team member sessions
      await fetchTeamMemberSessions(selectedTeam.id);
    } catch (err) {
      console.error('Error updating team activity:', err);
      setError(err.message || 'Failed to update team activity');
    } finally {
      setLoading(false);
    }
  };
  
  // Handle team-wide business unit change
  const handleTeamBusinessUnit = async (businessUnit) => {
    if (!selectedTeam) return;
    
    try {
      setLoading(true);
      const response = await fetch(`${API_URL}/labor/teams/${selectedTeam.id}/business-unit`, {
        method: 'PUT',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${localStorage.getItem('token')}`
        },
        body: JSON.stringify({
          business_unit: businessUnit,
          notes: `Team business unit changed by manager ${currentUser.username}`
        })
      });
      
      if (!response.ok) {
        throw new Error('Failed to update team business unit');
      }
      
      setSuccess(`Team business unit changed to ${businessUnit}`);
      // Refresh team member sessions
      await fetchTeamMemberSessions(selectedTeam.id);
    } catch (err) {
      console.error('Error updating team business unit:', err);
      setError(err.message || 'Failed to update team business unit');
    } finally {
      setLoading(false);
    }
  };
  
  
  // WebSocket listeners for team updates
  useEffect(() => {
    if (!socket || !selectedTeam || !isTeamManager) return;
    
    // Listen for general clock status updates from team members
    const handleTeamClockStatusUpdate = (data) => {
      console.log('Team manager received clock status update:', data);
      
      // Check if the updated user is a member of the selected team
      if (teamMembers.some(member => member.employee_id == data.userId)) {
        console.log('Team member clock status updated, refreshing team data:', data.userId);
        fetchTeamMemberSessions(selectedTeam.id);
      }
    };
    
    // Listen for team member updates
    const handleTeamUpdate = (data) => {
      // Refresh team member sessions when any team member's status changes
      if (data.teamId === selectedTeam.id) {
        console.log('Team update received for team:', data.teamId);
        fetchTeamMemberSessions(selectedTeam.id);
      }
    };
    
    // Listen for both team-specific and general clock updates
    socket.on('update-clock-status', handleTeamClockStatusUpdate);
    socket.on('team-update', handleTeamUpdate);
    
    return () => {
      socket.off('update-clock-status', handleTeamClockStatusUpdate);
      socket.off('team-update', handleTeamUpdate);
    };
  }, [socket, selectedTeam, teamMembers, isTeamManager]);
  
  return (
    <div className="space-y-6">
      {/* Socket Connection Status */}
      <div>
        <div className={`px-3 py-1 rounded-md inline-flex items-center text-sm ${isConnected ? 'bg-green-900/30 text-green-300' : 'bg-red-900/30 text-red-300'}`}>
          <span className={`w-2 h-2 rounded-full mr-2 ${isConnected ? 'bg-green-400' : 'bg-red-400'}`}></span>
          {isConnected ? 'Connected' : 'Disconnected'} 
        </div>
      </div>
      
      {/* Main Content - Side by Side on Desktop */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        {/* Main Clock In/Out Card */}
        <div className="bg-slate-800 border border-slate-700 rounded-lg shadow-sm p-6">
        <div className="flex flex-row items-center justify-between mb-6">
          <div className="max-w-[60%]">
            <h2 className="text-xl font-semibold text-slate-200 truncate">Clock In/Out</h2>
            <p className="text-slate-400 text-sm truncate">
              {currentUser && `Welcome, ${currentUser.fullName || currentUser.username}`}
            </p>
          </div>
          <div className="flex flex-col items-end">
            <p className="text-xl font-semibold text-slate-200">{formatTime(currentTime)}</p>
            <p className="text-xs sm:text-sm text-slate-400 truncate">{formatDate(currentTime)}</p>
          </div>
        </div>

        {error && (
          <div className="mb-4 bg-red-900/30 border border-red-800 text-red-300 px-4 py-3 rounded-md">
            {error}
          </div>
        )}
        
        {success && (
          <div className="mb-4 bg-green-900/30 border border-green-800 text-green-300 px-4 py-3 rounded-md">
            {success}
          </div>
        )}

        <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-6">
          <div>
            <label htmlFor="activity" className="block text-sm font-medium text-slate-300 mb-2">
              Activity
            </label>
            <div className="relative">
              <Briefcase className="absolute left-3 top-1/2 transform -translate-y-1/2 text-slate-400 w-5 h-5" />
              <select
                id="activity"
                value={selectedActivity}
                onChange={handleActivityChange}
                className={`w-full pl-10 pr-4 py-2 rounded-md text-slate-200 
                      focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent appearance-none
                      ${!selectedActivity && clockedIn && !onBreak && !onLunch ? 
                        'border-2 border-red-500 bg-red-500/10 animate-pulse shadow-lg shadow-red-500/20' : 
                        'border border-slate-600 bg-slate-700'}`}
                disabled={!clockedIn || onBreak || onLunch}
              >
                <option value="" className="bg-slate-700 text-slate-200">Select Activity</option>
                {activities.filter(activity => activity.is_active).map((activity) => (
                  <option key={activity.id} value={activity.name} className="bg-slate-700 text-slate-200">{activity.name}</option>
                ))}
              </select>
              <div className="absolute inset-y-0 right-0 flex items-center px-2 pointer-events-none">
                <svg className="w-5 h-5 text-slate-400" fill="none" stroke="currentColor" viewBox="0 0 24 24" xmlns="http://www.w3.org/2000/svg">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M19 9l-7 7-7-7"></path>
                </svg>
              </div>
            </div>
          </div>

          <div>
            <label htmlFor="businessUnit" className="block text-sm font-medium text-slate-300 mb-2">
              Business Unit
            </label>
            <div className="relative">
              <Building2 className="absolute left-3 top-1/2 transform -translate-y-1/2 text-slate-400 w-5 h-5" />
              <select
                id="businessUnit"
                value={selectedBusinessUnit}
                onChange={handleBusinessUnitChange}
                className={`w-full pl-10 pr-4 py-2 rounded-md text-slate-200 
                      focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent appearance-none
                      ${!selectedBusinessUnit && clockedIn && !onBreak && !onLunch ? 
                        'border-2 border-red-500 bg-red-500/10 animate-pulse shadow-lg shadow-red-500/20' : 
                        'border border-slate-600 bg-slate-700'}`}
                disabled={!clockedIn || onBreak || onLunch}
              >
                <option value="" className="bg-slate-700 text-slate-200">Select Business Unit</option>
                {businessUnits.filter(unit => unit.is_active).map((unit) => (
                  <option key={unit.id} value={unit.identifier} className="bg-slate-700 text-slate-200">{unit.name} - {unit.identifier}</option>
                ))}
              </select>
              <div className="absolute inset-y-0 right-0 flex items-center px-2 pointer-events-none">
                <svg className="w-5 h-5 text-slate-400" fill="none" stroke="currentColor" viewBox="0 0 24 24" xmlns="http://www.w3.org/2000/svg">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M19 9l-7 7-7-7"></path>
                </svg>
              </div>
            </div>
          </div>
        </div>

        {/* Status Messages */}
        <div className="mb-6">
          {clockInTime && clockedIn && (
            <div className="flex items-center text-green-400 mb-2">
              <Clock className="w-4 h-4 mr-2" />
              <span>Clocked in at {formatTime(clockInTime)}</span>
            </div>
          )}
          {clockOutTime && !clockedIn && (
            <div className="flex items-center text-red-400 mb-2">
              <Clock className="w-4 h-4 mr-2" />
              <span>Clocked out at {formatTime(clockOutTime)}</span>
            </div>
          )}
          {onBreak && (
            <div className="flex items-center text-yellow-400">
              <Coffee className="w-4 h-4 mr-2" />
              <span>On break since {formatTime(breakStartTime)}</span>
            </div>
          )}
          {onLunch && (
            <div className="flex items-center text-orange-400">
              <Coffee className="w-4 h-4 mr-2" />
              <span>On lunch since {formatTime(lunchStartTime)}</span>
            </div>
          )}
        </div>

        {/* Action Buttons */}
        <div className="flex flex-col sm:flex-row gap-4">
          <button
            onClick={handleClockIn}
            disabled={loading}
            className={`flex-1 flex items-center justify-center px-4 py-3 rounded-md text-white font-medium transition-colors ${
              clockedIn
                ? 'bg-red-600 hover:bg-red-700'
                : 'bg-green-600 hover:bg-green-700'
            } ${loading ? 'opacity-50 cursor-not-allowed' : ''}`}
          >
            {loading ? (
              <svg className="animate-spin h-5 w-5 mr-2 text-white" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
              </svg>
            ) : (
              <Clock className="w-5 h-5 mr-2" />
            )}
            {clockedIn ? 'Clock Out' : 'Clock In'}
          </button>
          
          {clockedIn && (
            <div className="flex-1 grid grid-cols-2 gap-2">
              <button
                onClick={handleBreak}
                disabled={loading || onLunch}
                className={`flex items-center justify-center px-2 py-3 rounded-md text-white font-medium transition-colors ${
                  onBreak
                    ? 'bg-yellow-600 hover:bg-yellow-700'
                    : 'bg-blue-600 hover:bg-blue-700'
                } ${(loading || onLunch) ? 'opacity-50 cursor-not-allowed' : ''}`}
              >
                <Coffee className="w-5 h-5 mr-1" />
                {onBreak ? 'End Break' : 'Take Break'}
              </button>
              
              <button
                onClick={handleLunch}
                disabled={loading || onBreak}
                className={`flex items-center justify-center px-2 py-3 rounded-md text-white font-medium transition-colors ${
                  onLunch
                    ? 'bg-orange-600 hover:bg-orange-700'
                    : 'bg-purple-600 hover:bg-purple-700'
                } ${(loading || onBreak) ? 'opacity-50 cursor-not-allowed' : ''}`}
              >
                <Coffee className="w-5 h-5 mr-1" />
                {onLunch ? 'End Lunch' : 'Take Lunch'}
              </button>
            </div>
          )}
        </div>
        </div>
        
        {/* Team Manager Dashboard - Compact Version */}
        {isTeamManager && (
          <div className="bg-slate-800 border border-slate-700 rounded-lg shadow-sm p-4">
            <TeamManagerDashboard
              isTeamManager={isTeamManager}
              managedTeams={managedTeams}
              selectedTeam={selectedTeam}
              setSelectedTeam={(team) => {
                setSelectedTeam(team);
                if (team) {
                  fetchTeamMemberSessions(team.id);
                }
              }}
              teamMembers={teamMembers}
              teamMemberSessions={teamMemberSessions}
              loadingTeamData={loadingTeamData}
              handleTeamMemberBreak={handleTeamMemberBreak}
              handleTeamMemberLunch={handleTeamMemberLunch}
              handleTeamMemberActivityChange={handleTeamMemberActivityChange}
              handleTeamMemberBusinessUnitChange={handleTeamMemberBusinessUnitChange}
              activities={activities}
              businessUnits={businessUnits}
              handleTeamActivity={handleTeamActivity}
              handleTeamBusinessUnit={handleTeamBusinessUnit}
              isCompact={true}
            />
          </div>
        )}
      </div>
      
    </div>
  );
};

export default ClockIn;
